import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

const emptyForm = {
  id: '',
  name: '',
  players: 4,
  location: '',
  startDate: '',
  endDate: '',
  time: '15h00 à 16h30',
  code: '',
  category: '',
  day: 'samedi',

  // Logo / image affichée dans LeagueList
  picture: '',

  // Grande image affichée dans LeagueRegistration
  registrationPicture: '',

  terrain1: 'Terrain 1',
  terrain2: 'Terrain 2',
  price: 80,
  eloMin: 1,
  eloMax: 9000,
  visible: true,
  archived: false,
};

const AdminLeagueCatalog = () => {
  const [form, setForm] = useState(emptyForm);
  const [leagues, setLeagues] = useState([]);
  const [teamsCounts, setTeamsCounts] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [registrationImageFile, setRegistrationImageFile] = useState(null);
  const [uploadingRegistrationImage, setUploadingRegistrationImage] = useState(false);

  const isBusy = loading || uploadingRegistrationImage;

  const loadLeagues = async () => {
    try {
      setLoadingList(true);

      const snapshot = await getDocs(collection(db, 'league_registrations'));

      const data = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const id = Number(data.id ?? docSnap.id);

        return {
          ...data,
          _docId: docSnap.id,
          id,
          dateRange: data.dateRange || [
            data.startDate || '',
            data.endDate || data.startDate || '',
          ],
        };
      });

      data.sort((a, b) => {
        const dateA = a.dateRange?.[0] ? new Date(a.dateRange[0]).getTime() : 0;
        const dateB = b.dateRange?.[0] ? new Date(b.dateRange[0]).getTime() : 0;

        if (dateA !== dateB) return dateA - dateB;

        return Number(a.id || 0) - Number(b.id || 0);
      });

      setLeagues(data);
    } catch (error) {
      console.error('Erreur chargement league_registrations:', error);
      alert('Erreur lors du chargement des ligues.');
    } finally {
      setLoadingList(false);
    }
  };

  const loadTeamsCounts = async () => {
    try {
      const res = await fetch(
        'https://us-central1-mendyx-adafe.cloudfunctions.net/getAllLeagueParticipantsCounts'
      );

      const data = await res.json();

      if (res.ok && data.counts) {
        const parsedCounts = {};

        for (const id in data.counts) {
          parsedCounts[parseInt(id)] = data.counts[id];
        }

        setTeamsCounts(parsedCounts);
      }
    } catch (error) {
      console.warn('Impossible de charger les compteurs de ligues:', error);
    }
  };

  useEffect(() => {
    loadLeagues();
    loadTeamsCounts();
  }, []);

  const nextId = useMemo(() => {
    if (!leagues.length) return 1;

    return Math.max(...leagues.map((league) => Number(league.id || 0)), 0) + 1;
  }, [leagues]);

  const filteredLeagues = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return leagues;

    return leagues.filter((league) => (
      String(league.id || '').includes(term) ||
      String(league.name || '').toLowerCase().includes(term) ||
      String(league.location || '').toLowerCase().includes(term) ||
      String(league.category || '').toLowerCase().includes(term) ||
      String(league.day || '').toLowerCase().includes(term) ||
      String(league.code || '').toLowerCase().includes(term)
    ));
  }, [search, leagues]);

  const getLeagueDocId = (league) => {
    return String(league._docId || league.id);
  };

  const scrollToForm = () => {
    setTimeout(() => {
      document
        .getElementById('admin-league-form')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setRegistrationImageFile(null);
  };

  const generateCode = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const randomLetters = Array.from({ length: 3 }, () =>
      letters[Math.floor(Math.random() * letters.length)]
    ).join('');

    const randomNumbers = Math.floor(1000 + Math.random() * 9000);

    return `${randomLetters}${randomNumbers}`;
  };

  const setNextLeague = () => {
    setForm((prev) => ({
      ...prev,
      id: nextId,
      name: `Ligue Padel - Bois du Loup | P100 Hommes`,
      code: generateCode(),
      picture: 'BDL.jfif',
    }));

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const generateLeagueCode = () => {
    setForm((prev) => ({
      ...prev,
      code: generateCode(),
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    if (!form.id) return 'ID obligatoire.';
    if (!form.name.trim()) return 'Nom obligatoire.';
    if (!form.location.trim()) return 'Lieu obligatoire.';
    if (!form.startDate) return 'Date de début obligatoire.';
    if (!form.endDate) return 'Date de fin obligatoire.';
    if (!form.time.trim()) return 'Horaire obligatoire.';
    if (!form.day.trim()) return 'Jour obligatoire.';
    if (!form.players) return 'Nombre d’équipes obligatoire.';
    if (!form.category.trim()) return 'Catégorie obligatoire.';
    if (!form.code.trim()) return 'Code obligatoire.';

    if (!form.picture.trim()) {
      return 'Logo / image carte obligatoire. Exemple : BDL.jfif.';
    }

    if (new Date(form.endDate) < new Date(form.startDate)) {
      return 'La date de fin ne peut pas être avant la date de début.';
    }

    return null;
  };

  const uploadRegistrationPicture = async () => {
    if (!registrationImageFile) {
      return form.registrationPicture.trim();
    }

    if (!form.id) {
      alert("Mets d'abord un ID de ligue avant d'uploader une image.");
      return null;
    }

    try {
      setUploadingRegistrationImage(true);

      const storage = getStorage(
        undefined,
        'gs://mendyx-adafe.firebasestorage.app'
      );

      const extension = registrationImageFile.name.split('.').pop() || 'jpg';

      const cleanName = registrationImageFile.name
        .replace(/\.[^/.]+$/, '')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .toLowerCase();

      const fileName = `league-registration-${form.id}-${Date.now()}-${cleanName}.${extension}`;

      const storageRef = ref(
        storage,
        `leagues/${form.id}/registration/${fileName}`
      );

      await uploadBytes(storageRef, registrationImageFile, {
        contentType: registrationImageFile.type || 'image/jpeg',
      });

      const downloadURL = await getDownloadURL(storageRef);

      setForm((prev) => ({
        ...prev,
        registrationPicture: downloadURL,
      }));

      setRegistrationImageFile(null);

      return downloadURL;
    } catch (error) {
      console.error('Erreur upload image inscription ligue:', error);
      alert("Erreur lors de l'upload de l'image d'inscription.");
      return null;
    } finally {
      setUploadingRegistrationImage(false);
    }
  };

  const buildLeaguePayload = (registrationPictureUrl) => {
    return {
      id: Number(form.id),
      name: form.name.trim(),
      players: Number(form.players),
      location: form.location.trim(),
      dateRange: [form.startDate, form.endDate],
      startDate: form.startDate,
      endDate: form.endDate,
      time: form.time.trim(),
      code: form.code.trim(),
      category: form.category.trim(),
      day: form.day.trim(),

      // Logo affiché dans LeagueList
      picture: form.picture.trim(),

      // Grande image affichée dans LeagueRegistration
      registrationPicture: registrationPictureUrl || '',

      terrain1: form.terrain1.trim(),
      terrain2: form.terrain2.trim(),
      price: Number(form.price),
      eloMin: Number(form.eloMin),
      eloMax: Number(form.eloMax),
      visible: Boolean(form.visible),
      archived: Boolean(form.archived),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMessage = validateForm();

    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    try {
      setLoading(true);

      const registrationPictureUrl = await uploadRegistrationPicture();

      if (registrationPictureUrl === null) {
        return;
      }

      const payload = buildLeaguePayload(registrationPictureUrl);
      const documentId = String(payload.id);

      await setDoc(
        doc(db, 'league_registrations', documentId),
        payload,
        { merge: true }
      );

      alert(`✅ Ligue ${payload.id} enregistrée dans league_registrations.`);

      setForm(emptyForm);
      setRegistrationImageFile(null);
      await loadLeagues();
    } catch (error) {
      console.error('Erreur enregistrement ligue:', error);
      alert("Erreur lors de l'enregistrement de la ligue.");
    } finally {
      setLoading(false);
    }
  };

  const editLeague = (league) => {
    setForm({
      id: league.id ?? '',
      name: league.name ?? '',
      players: league.players ?? 4,
      location: league.location ?? '',
      startDate: league.dateRange?.[0] ?? league.startDate ?? '',
      endDate: league.dateRange?.[1] ?? league.endDate ?? league.startDate ?? '',
      time: league.time ?? '15h00 à 16h30',
      code: league.code ?? '',
      category: league.category ?? '',
      day: league.day ?? 'samedi',
      picture: league.picture ?? '',
      registrationPicture: league.registrationPicture ?? '',
      terrain1: league.terrain1 ?? 'Terrain 1',
      terrain2: league.terrain2 ?? 'Terrain 2',
      price: league.price ?? 80,
      eloMin: league.eloMin ?? 1,
      eloMax: league.eloMax ?? 9000,
      visible: league.visible ?? true,
      archived: league.archived ?? false,
    });

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const duplicateLeague = (league) => {
    const startDate = league.dateRange?.[0] ?? league.startDate ?? '';
    const endDate = league.dateRange?.[1] ?? league.endDate ?? startDate;

    setForm({
      id: nextId,
      name: league.name ? `${league.name} - copie` : `Ligue Padel n°${nextId}`,
      players: league.players ?? 4,
      location: league.location ?? '',
      startDate,
      endDate,
      time: league.time ?? '15h00 à 16h30',
      code: generateCode(),
      category: league.category ?? '',
      day: league.day ?? 'samedi',
      picture: league.picture ?? '',
      registrationPicture: league.registrationPicture ?? '',
      terrain1: league.terrain1 ?? 'Terrain 1',
      terrain2: league.terrain2 ?? 'Terrain 2',
      price: league.price ?? 80,
      eloMin: league.eloMin ?? 1,
      eloMax: league.eloMax ?? 9000,
      visible: true,
      archived: false,
    });

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const toggleVisibility = async (league) => {
    try {
      setLoading(true);

      await setDoc(
        doc(db, 'league_registrations', getLeagueDocId(league)),
        {
          visible: !Boolean(league.visible),
        },
        { merge: true }
      );

      await loadLeagues();
    } catch (error) {
      console.error('Erreur visibilité ligue:', error);
      alert('Erreur lors du changement de visibilité.');
    } finally {
      setLoading(false);
    }
  };

  const archiveLeague = async (league) => {
    const confirmArchive = window.confirm(
      `Archiver la ligue ${league.id} ? Elle ne sera plus visible sur le site.`
    );

    if (!confirmArchive) return;

    try {
      setLoading(true);

      await setDoc(
        doc(db, 'league_registrations', getLeagueDocId(league)),
        {
          archived: true,
          visible: false,
        },
        { merge: true }
      );

      await loadLeagues();
    } catch (error) {
      console.error('Erreur archivage ligue:', error);
      alert("Erreur lors de l'archivage.");
    } finally {
      setLoading(false);
    }
  };

  const deleteLeague = async (league) => {
    const confirmDelete = window.confirm(
      `Supprimer définitivement la fiche de la ligue ${league.id} ? Attention : les équipes, calendriers ou scores liés ne sont pas automatiquement supprimés.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);

      await deleteDoc(doc(db, 'league_registrations', getLeagueDocId(league)));

      await loadLeagues();
    } catch (error) {
      console.error('Erreur suppression ligue:', error);
      alert('Erreur lors de la suppression de la ligue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#800020]">
          Admin - Gestion des ligues
        </h1>

        <p className="text-gray-600 mt-2">
          Ajoute, modifie, duplique ou archive les ligues dans league_registrations.
        </p>

        <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          La collection league_registrations devient le catalogue des ligues.
          Les inscriptions, calendriers et scores restent liés à l’ID de la ligue.
        </div>
      </div>

      <form
        id="admin-league-form"
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-2xl p-4 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div className="md:col-span-2 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            Ajouter / Modifier une ligue
          </h2>

          <div className="flex flex-col md:flex-row gap-2">
            <button
              type="button"
              onClick={setNextLeague}
              className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm"
            >
              Pré-remplir prochain ID : {nextId}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-full bg-gray-200 text-gray-800 text-sm"
            >
              Vider le formulaire
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">ID</label>
          <input
            name="id"
            type="number"
            value={form.id}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="3"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Nom</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Ligue Padel - Bois du Loup | P200 Hommes"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold mb-1">Lieu</label>
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Bois du Loup, Chau. de Ligny 70, 5140 Sombreffe"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Date début</label>
          <input
            name="startDate"
            type="date"
            value={form.startDate}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Date fin</label>
          <input
            name="endDate"
            type="date"
            value={form.endDate}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Jour</label>
          <select
            name="day"
            value={form.day}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          >
            <option value="lundi">lundi</option>
            <option value="mardi">mardi</option>
            <option value="mercredi">mercredi</option>
            <option value="jeudi">jeudi</option>
            <option value="vendredi">vendredi</option>
            <option value="samedi">samedi</option>
            <option value="dimanche">dimanche</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Horaire</label>
          <input
            name="time"
            value={form.time}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="15h00 à 16h30"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Équipes max</label>
          <input
            name="players"
            type="number"
            value={form.players}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="4"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Prix</label>
          <input
            name="price"
            type="number"
            value={form.price}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="80"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Catégorie</label>
          <input
            name="category"
            value={form.category}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="P200 Hommes"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">ELO min</label>
          <input
            name="eloMin"
            type="number"
            value={form.eloMin}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="1"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">ELO max</label>
          <input
            name="eloMax"
            type="number"
            value={form.eloMax}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="9000"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Terrain 1</label>
          <input
            name="terrain1"
            value={form.terrain1}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Terrain 1"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Terrain 2</label>
          <input
            name="terrain2"
            value={form.terrain2}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Terrain 2"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Code ligue</label>
          <div className="flex gap-2">
            <input
              name="code"
              value={form.code}
              onChange={handleChange}
              className="w-full border rounded-lg p-2"
              placeholder="XYZ1214"
              required
            />

            <button
              type="button"
              onClick={generateLeagueCode}
              className="px-3 rounded-lg bg-gray-900 text-white text-sm"
            >
              Générer
            </button>
          </div>
        </div>

        <div className="md:col-span-2 border rounded-xl p-4 bg-gray-50">
          <label className="block text-sm font-semibold mb-1">
            Logo / image carte de la ligue
          </label>

          <input
            name="picture"
            value={form.picture}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 bg-white"
            placeholder="BDL.jfif"
            required
          />

          <p className="text-xs text-gray-500 mt-1">
            Sert uniquement à la carte dans la liste des ligues. Exemple : BDL.jfif.
          </p>

          {form.picture && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Aperçu logo / carte :</p>

              <img
                src={form.picture}
                alt="Aperçu logo ligue"
                className="w-32 h-32 object-contain rounded-lg border bg-white"
              />
            </div>
          )}
        </div>

        <div className="md:col-span-2 border rounded-xl p-4 bg-red-50">
          <label className="block text-sm font-semibold mb-1">
            Image du formulaire d’inscription
          </label>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setRegistrationImageFile(e.target.files?.[0] || null)}
            className="w-full border rounded-lg p-2 bg-white"
          />

          <p className="text-xs text-gray-500 mt-1">
            Cette image sera affichée à gauche du formulaire d’inscription de la ligue.
          </p>

          {registrationImageFile && (
            <div className="mt-2 text-sm text-gray-700">
              Nouvelle image sélectionnée : <strong>{registrationImageFile.name}</strong>
            </div>
          )}

          {form.registrationPicture && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">
                Image actuelle du formulaire :
              </p>

              <img
                src={form.registrationPicture}
                alt="Aperçu inscription ligue"
                className="w-48 h-48 object-contain rounded-lg border bg-white"
              />

              <label className="block text-xs font-semibold text-gray-500 mt-3 mb-1">
                URL enregistrée
              </label>

              <input
                name="registrationPicture"
                value={form.registrationPicture}
                onChange={handleChange}
                className="w-full border rounded-lg p-2 text-xs bg-white"
                placeholder="URL Firebase Storage ou /league5.jpg"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            name="visible"
            type="checkbox"
            checked={form.visible}
            onChange={handleChange}
          />
          <label>Visible sur le site</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            name="archived"
            type="checkbox"
            checked={form.archived}
            onChange={handleChange}
          />
          <label>Archivée</label>
        </div>

        <button
          type="submit"
          disabled={isBusy}
          className="md:col-span-2 bg-[#800020] text-white rounded-full py-3 font-semibold shadow-lg disabled:opacity-60"
        >
          {isBusy ? 'Enregistrement...' : 'Enregistrer la ligue'}
        </button>
      </form>

      <div className="bg-white shadow-lg rounded-2xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Ligues dans league_registrations
          </h2>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg p-2 w-full md:w-80"
            placeholder="Recherche ID, lieu, catégorie, code..."
          />
        </div>

        {loadingList ? (
          <p className="text-gray-500">Chargement...</p>
        ) : filteredLeagues.length === 0 ? (
          <p className="text-gray-500">
            Aucune ligue trouvée dans league_registrations.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredLeagues.map((league) => {
              const count = teamsCounts[league.id] || 0;

              return (
                <div
                  key={getLeagueDocId(league)}
                  className={`border rounded-xl p-4 ${
                    league.archived ? 'bg-gray-100 opacity-70' : 'bg-white'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex gap-4">
                      {league.picture && (
                        <img
                          src={league.picture}
                          alt={league.name}
                          className="hidden sm:block w-20 h-20 object-contain rounded-lg border bg-gray-50"
                        />
                      )}

                      <div>
                        <div className="font-bold text-lg text-[#800020]">
                          #{league.id} - {league.name}
                        </div>

                        <div className="text-sm text-gray-600 mt-1">
                          {league.dateRange?.[0]} → {league.dateRange?.[1]} | {league.day} | {league.time}
                        </div>

                        <div className="text-sm text-gray-600">
                          {league.location}
                        </div>

                        <div className="text-sm mt-1">
                          {count} / {league.players} équipes | {league.price}€ | {league.category}
                        </div>

                        <div className="text-xs text-gray-500 mt-1">
                          Code : {league.code || 'Aucun'} | ELO : {league.eloMin} - {league.eloMax}
                        </div>

                        <div className="text-xs text-gray-500 mt-1 break-all">
                          Logo carte : {league.picture || 'Aucun'}
                        </div>

                        <div className="text-xs text-gray-500 mt-1 break-all">
                          Image formulaire : {league.registrationPicture || 'Aucune'}
                        </div>

                        <div className="text-xs text-gray-500 mt-1">
                          Visible : {league.visible ? 'oui' : 'non'} | Archivée : {league.archived ? 'oui' : 'non'}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => editLeague(league)}
                        className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-60"
                      >
                        Modifier
                      </button>

                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => duplicateLeague(league)}
                        className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-60"
                      >
                        Dupliquer
                      </button>

                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => toggleVisibility(league)}
                        className="px-3 py-2 rounded-lg bg-yellow-500 text-white text-sm disabled:opacity-60"
                      >
                        {league.visible ? 'Masquer' : 'Afficher'}
                      </button>

                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => archiveLeague(league)}
                        className="px-3 py-2 rounded-lg bg-gray-700 text-white text-sm disabled:opacity-60"
                      >
                        Archiver
                      </button>

                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => deleteLeague(league)}
                        className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-60"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800 text-sm">
        Conseil : utilise plutôt “Archiver” que “Supprimer” si une ligue a déjà des équipes, un calendrier ou des scores.
      </div>
    </div>
  );
};

export default AdminLeagueCatalog;